import { useRef, useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  AccountLifecycleSection,
  Button,
  ConnectedToolsSection,
  EmptyState,
  LocalePreferencesSection,
  Skeleton,
} from '@/components';
import { ChangeRegistrationManager } from '@/components/integrations/ChangeRegistrationManager';
import { ProfilePanel } from '@/components/profile/ProfilePanel';
import { announce } from '@/lib/announce';
import { boardsPageQueryOptions } from '@/lib/boards-page';
import { boardListsQueryOptions } from '@/lib/lists';
import {
  useChangeRegistrations,
  useCreateChangeRegistration,
  useDeleteChangeRegistration,
  useReplaceChangeRegistrationEventFilter,
  useResumeChangeRegistration,
  useRotateChangeRegistrationSigningSecret,
} from '@/lib/change-registrations';
import {
  useConnectedApps,
  useCreateConnectedAppGrant,
  useRevealConnectedAppToken,
  useRevokeConnectedAppGrant,
} from '@/lib/external-access';
import {
  localePreferencesQueryOptions,
  useSetLocaleLanguage,
  useSetTimezoneOverride,
} from '@/lib/locale-preferences';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  avatarFileRejection,
  useDeactivateAccount,
  useDeleteAccount,
  useUpdateAvatar,
  useUpdateBio,
  useUpdateDisplayName,
  useUploadAvatar,
} from '@/lib/profile';
import { SIGN_IN_PATH, signInHref, useSession } from '@/lib/session';
import { useNavigate } from '@tanstack/react-router';

/**
 * `/profile` — `FE-06.2`, `FR-PERM-002`.
 *
 * The composition site for `ProfilePanel`: the panel is presentational (props
 * in, callbacks out, no `announce()`) and this file owns the session, the
 * three mutations, the mapping from `ApiError` to the panel's plain-string
 * `error` props, and the save announcements.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE HOLE THIS ROUTE HAS TO PLUG, AND WHY IT EXISTS
 * ---------------------------------------------------------------------------
 * `main.tsx`'s `QueryCache.onError` sends any `unauthenticated` failure to
 * `/sign-in` — **except** the shell's own `['me']` probe, which is excluded by
 * key so that `/sign-in` cannot redirect to itself (brief §5.1; the
 * `throwOnError: false` guard `entry-surfaces.md` D12 proposes does not work,
 * because `query-core` fires the cache callback before `throwOnError` is ever
 * consulted).
 *
 * This route reads that same excluded query and issues **no other request**.
 * So a signed-out visitor to `/profile` gets no automatic bounce from
 * anywhere: not from the global binding (excluded), and not from a second
 * query (there isn't one). Without the branch below, `/profile` would render
 * nothing at all for them, forever, with nothing red anywhere. It is the one
 * route where the §5.1 exclusion has a visible consequence.
 *
 * The branch does **not** redirect. It renders an `EmptyState` with a **Sign
 * in** action carrying `?next=/profile`, because a silent bounce from a URL a
 * member typed is indistinguishable from a broken link, and D10 already
 * settled that this product tells a visitor what happened and offers the way
 * forward rather than moving them.
 *
 * ---------------------------------------------------------------------------
 * BRANCH ORDER: `isSignedOut` FIRST, ALWAYS
 * ---------------------------------------------------------------------------
 * `useSession()` can return a non-null `profile` **and** `isSignedOut: true` —
 * TanStack Query keeps the last successful `data` beside a new error, which is
 * exactly the shape of an expired session. Rendering the panel in that state
 * would let the member edit a profile whose session no longer exists: every
 * save 401s, and the form they are looking at is a lie they will act on. So
 * `isSignedOut` is tested before `profile`, here and in `__root.tsx` and
 * `sign-in.tsx`.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE MAPS THE ERRORS AND `lib/profile.ts` DOES NOT
 * ---------------------------------------------------------------------------
 * `ProfilePanelProps.error` takes plain strings; the mutations surface
 * `ApiError`. There is no mapper in `lib/profile.ts` by design — the panel is
 * reusable and the data layer is presentation-free, so the route is the only
 * layer that knows both. `ApiError.message` is already the server's own
 * member-facing sentence (`apiErrorSchema`'s `message`), so the mapping is a
 * field read rather than a translation table; the translation table that
 * *does* exist is `lib/auth.ts`'s, and it exists only because Better Auth's
 * codes are not sentences.
 *
 * ---------------------------------------------------------------------------
 * D11 — SAVES ANNOUNCE, FAILURES DO NOT
 * ---------------------------------------------------------------------------
 * A successful save has no `role="alert"`/`role="status"` node of its own —
 * the field simply now holds the saved value — so `announce()` is the correct
 * and only channel for it. A *failed* save renders through the field's own
 * `error` prop and is not announced from here. One event, one channel.
 *
 * The announcement is passed per-call (`mutate(body, { onSuccess })`) rather
 * than added to `useMutation`: the hooks in `lib/profile.ts` already declare
 * an `onSuccess` that writes the response into `['me']` (§5.5), and both the
 * options-level and the call-level callback fire — declaring a second one on
 * the hook would replace the cache write and silently break the rename that
 * `FR-PERM-002` AC2 asserts.
 */
export const Route = createFileRoute('/profile')({
  component: ProfileRoute,
});

/** The panel's own geometry, so the page does not jump when it arrives. */
function ProfilePending() {
  return (
    <div aria-busy="true" className="flex flex-col gap-6">
      <Skeleton shape="line" width="10rem" />
      <Skeleton shape="block" count={3} />
    </div>
  );
}

/**
 * The page frame, and it wraps **every** state rather than only the loaded
 * one.
 *
 * ⚠ §7 fixes an `h1` named `Your profile` for this route, and
 * `EmptyState.headingLevel` accepts only `2 | 3 | 4` — deliberately, because a
 * primitive cannot know its place in the outline. So an `EmptyState` returned
 * *instead* of this frame ships a page whose highest heading is a level 2 with
 * no level 1 above it: the exact defect `headingLevel` was added for in `C1`,
 * inverted. Wrapping every branch is what keeps the outline honest in the
 * three states nobody screenshots — signed out, pending, and a failed probe.
 */
function ProfilePage({ children }: { children: ReactNode }) {
  // FE-80.1 — the frame reaches `t` itself rather than taking the title as a
  // prop: it wraps FOUR branches (signed out, pending, failed probe, loaded)
  // and a prop would be four call sites for one heading.
  const { t } = useInterfaceLanguage();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 py-8">
      {/*
        The `h1` is the route's, not the panel's: `ProfilePanel` opens at `h2`
        because it is a fragment of a page and cannot know its place in the
        outline (the same argument `EmptyState.headingLevel` carries). §7 fixes
        the text.
      */}
      <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
        {t('profile.page.title')}
      </h1>
      {children}
    </div>
  );
}

function ProfileRoute() {
  const navigate = useNavigate();
  const { t } = useInterfaceLanguage();
  const { profile, isPending, isSignedOut, error } = useSession();
  const displayName = useUpdateDisplayName();
  const bio = useUpdateBio();
  const avatar = useUpdateAvatar();
  /*
   * FE-44.2 — the file picker. `t` is passed IN rather than read inside the
   * hook: `lib/profile.ts` takes its translator as a required first parameter
   * everywhere, because a module that reaches the context itself renders
   * `fallbackCatalogue` English *successfully* when no provider is mounted.
   */
  const avatarUpload = useUploadAvatar(t);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarFileRejected, setAvatarFileRejected] = useState<string | null>(null);
  /*
   * The input is cleared through the DOM node rather than by making it a
   * controlled component, because a file input's `value` cannot be set to
   * anything but the empty string — the same reason `CardAttachmentSection`
   * keeps a ref for exactly this.
   */
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null);
  const deactivateAccount = useDeactivateAccount();
  const deleteAccount = useDeleteAccount();
  const profileReady = !isSignedOut && !isPending && profile !== null;
  const connectedApps = useConnectedApps(profileReady);
  const changeRegistrations = useChangeRegistrations(profileReady);
  const boardsPage = useQuery({
    ...boardsPageQueryOptions('open'),
    enabled: profileReady,
  });
  const createConnectedApp = useCreateConnectedAppGrant();
  const revokeConnectedApp = useRevokeConnectedAppGrant();
  const revealConnectedAppToken = useRevealConnectedAppToken();
  const createChangeRegistration = useCreateChangeRegistration();
  const deleteChangeRegistration = useDeleteChangeRegistration();
  const rotateChangeRegistrationSecret = useRotateChangeRegistrationSigningSecret();
  const resumeChangeRegistration = useResumeChangeRegistration();
  const replaceChangeRegistrationEventFilter = useReplaceChangeRegistrationEventFilter();
  const [connectedToolToken, setConnectedToolToken] = useState<string | null>(null);
  const [connectedToolErrors, setConnectedToolErrors] = useState<Record<string, string>>(
    {},
  );
  const [changeRegistrationDeleteErrors, setChangeRegistrationDeleteErrors] = useState<
    Record<string, string>
  >({});
  const [changeRegistrationRotateErrors, setChangeRegistrationRotateErrors] = useState<
    Record<string, string>
  >({});
  const [changeRegistrationResumeErrors, setChangeRegistrationResumeErrors] = useState<
    Record<string, string>
  >({});
  const [changeRegistrationFilterErrors, setChangeRegistrationFilterErrors] = useState<
    Record<string, string>
  >({});
  /**
   * ⚠ THE ISSUED SIGNING SECRET LIVES HERE, IN ROUTE STATE, AND NOWHERE ELSE.
   * It reaches the browser exactly twice — the create `201` and the rotate
   * `200` — and `GET /me/change-registrations` answers `hasSigningSecret`
   * rather than the value, deliberately, so that a list refetch, a cached
   * response and a browser history entry are not copies of a signing key. The
   * mutation hooks strip it before the cache splice for the same reason, which
   * means this `useState` is the ONLY place it survives the callback.
   *
   * It is deliberately not `localStorage` either: the web unit suite has no
   * working one (Node 25 shadows jsdom's), so any assertion about a remembered
   * secret would be vacuously green — and a signing key is the last value that
   * should outlive the tab that was shown it.
   */
  const [issuedSigningSecret, setIssuedSigningSecret] = useState<{
    registrationId: string;
    signingSecret: string;
  } | null>(null);
  const localePreferences = useQuery({
    ...localePreferencesQueryOptions(),
    enabled: profileReady,
  });
  const setLocaleLanguage = useSetLocaleLanguage();
  const setTimezoneOverride = useSetTimezoneOverride();

  /**
   * FE-2 — THE LISTS OF EVERY BOARD THE FORM CAN TARGET, so a list
   * subscription stops requiring a pasted ULID.
   *
   * ⚠ `GET /boards/:id/lists`, NEVER `GET /boards/:id/contents`. The contents
   * route is the whole board face — every card of every list — and a board may
   * hold 5,000 of them (`FR-BOARD-035`), so populating a picker from it is the
   * one fetch this item was told not to make. The lists route carries lists
   * only.
   *
   * ⚠ WHEN THIS FIRES, STATED PLAINLY, BECAUSE THE FIRST VERSION OF THIS
   * COMMENT GOT IT WRONG. It fires on `/profile` PAGE LOAD for any member
   * holding an unrevoked grant — `ChangeRegistrationManager` is rendered
   * unconditionally below, and the target `<select>` this fills is in the
   * always-rendered create form, NOT behind the `<details>` the event filter
   * sits in. The control is on screen at load, which is what licenses fetching
   * at load. N is the member's WHOLE OPEN-BOARD COUNT and nothing bounds it:
   * `boardListQuerySchema` is `z.strictObject({ state })` — no limit, no
   * cursor.
   *
   * ⚠ DO NOT READ `useMoveDestinations` AS A PRECEDENT FOR THAT. It does fan
   * `useQueries` out over every board for members AND lists AND contents, but
   * ALL THREE of its callers gate it on an OPEN DIALOG
   * (`card-back/CardBack.tsx:467` `moveOpen || copyOpen || mirrorOpen`,
   * `board/BoardFace.tsx:1110-1113`, `routes/inbox.tsx:72`
   * `fileTarget !== null`), so the comparison licenses the per-board COST and
   * not the moment of the call.
   *
   * ⚠ GATED ON A USABLE GRANT, NOT MERELY ON `profileReady`. Without one the
   * manager refuses every submission with `reason.needsToken`, so fanning out
   * for a member who has never made a connected-app token would be N requests
   * to populate a control they cannot use.
   *
   * A SECOND, LARGER REDUCTION IS AVAILABLE AND WAS NOT TAKEN — it is open,
   * not ruled out. Deferring until the member first touches the target
   * `<select>` would take the common case (a member opening `/profile` for
   * anything else) to zero requests. It needs an interaction signal plumbed
   * from the manager back up here, because `profile.tsx` owns every query and
   * the component owns the interaction; that plumbing is a shape this phase
   * did not own. Anyone reopening this should reopen it as *that*, and can
   * reuse the `enabled` flag below unchanged.
   *
   * CARD TARGETS ARE DELIBERATELY NOT OFFERED, and the reason is measured
   * rather than an absence: `GET /lists/:id/cards` exists, but it declares
   * `querystring: z.strictObject({})` — no pagination of any kind — and no
   * per-list card cap is defined anywhere (`docs/notes/virtualisation-
   * deferral.md`: the board's 5,000 ceiling is defined and unenforced; the
   * per-list one is not defined at all). A card `<select>` would therefore
   * have an unbounded option count, which the product tie-breaker rules
   * against. `allowCustomTarget` already ships a `card` kind beside a
   * free-text id, so nothing that exists today is withdrawn.
   */
  const webhookBoards =
    boardsPage.data?.groups.flatMap((group) =>
      group.boards.map((board) => ({
        id: board.id,
        title: board.title,
        workspaceName: group.workspaceName,
      })),
    ) ?? [];
  const canFanOutForLists =
    profileReady &&
    (connectedApps.data?.connectedApps.some((grant) => grant.revokedAt === null) ??
      false);
  const webhookBoardLists = useQueries({
    queries: canFanOutForLists
      ? webhookBoards.map((board) => ({
          ...boardListsQueryOptions(board.id),
          enabled: canFanOutForLists,
        }))
      : [],
  });

  if (isSignedOut) {
    return (
      <ProfilePage>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('profile.page.signInTitle')}
          body={t('profile.page.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              /*
               * Built from `signInHref`, not hand-spelled, so `/profile` is
               * encoded the one way `safeNextPath` reads back. The literal
               * `search: ''` is correct: this route takes no search params, so
               * there is nothing of the current URL worth carrying.
               */
              href={signInHref({ pathname: '/profile', search: '' })}
            >
              {t('profile.page.signInAction')}
            </Button>
          }
        />
      </ProfilePage>
    );
  }

  if (isPending) {
    return (
      <ProfilePage>
        <ProfilePending />
      </ProfilePage>
    );
  }

  if (profile === null) {
    /*
     * Not pending, not signed out, and still no profile: the probe failed for
     * some other reason. `tone="error"` rather than `neutral` is
     * `FR-EXP-034`'s second edge case in as many words — a failed load must
     * never render the empty copy. No retry action: the shell's probe refetches
     * on window focus and on the next navigation, and a "Try again" here would
     * have to reach into a query this route does not own.
     */
    return (
      <ProfilePage>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('profile.page.loadFailedTitle')}
          /* The fallback is byte-identical to `myCards.genericError` and is
             deliberately NOT that key: the two pages can diverge, and a shared
             key would make diverging a copy change to a shipped surface. */
          body={error?.message ?? t('profile.page.genericError')}
        />
      </ProfilePage>
    );
  }

  // `exactOptionalPropertyTypes`: a prop typed `foo?: string` refuses an
  // explicit `undefined` — only an omitted key satisfies it. Each spread below
  // turns "no error" into "absent" rather than passing `undefined` through.
  const errorProps = {
    ...(displayName.error === null ? {} : { displayName: displayName.error.message }),
    ...(bio.error === null ? {} : { bio: bio.error.message }),
    ...(avatar.error === null ? {} : { avatar: avatar.error.message }),
  };
  /*
   * FE-44.2 — refuse at CHOOSE time, not at submit.
   *
   * The member learns the file is wrong while the picker is still the thing
   * they are looking at, and `avatarFile` is only ever set to something the
   * server will accept, so the Upload button cannot offer to send a file that
   * is already known to 422. The check itself is `lib/profile.ts`'s, and the
   * server's 422 remains authoritative — see that function's docstring.
   */
  function chooseAvatarFile(file: File | null) {
    if (file === null) {
      setAvatarFile(null);
      setAvatarFileRejected(null);
      return;
    }

    const rejection = avatarFileRejection(t, file);
    setAvatarFileRejected(rejection);
    setAvatarFile(rejection === null ? file : null);
  }

  function submitAvatarFile() {
    if (avatarFile === null) return;
    avatarUpload.mutate(avatarFile, {
      onSuccess: () => {
        /*
         * The SAME announcement the typed-URL save makes. One event — "this
         * member's avatar is now that image" — deserves one sentence, whichever
         * of the two controls produced it.
         */
        announce(t('profile.avatarSaved'));
        setAvatarFile(null);
        if (avatarFileInputRef.current !== null) avatarFileInputRef.current.value = '';
      },
    });
  }

  // A refusal this file made and a failure the server or the object store made
  // are the same thing to a reader: one line under the field, one at a time.
  // The local refusal wins because it is the newer fact — it was produced by
  // the file currently chosen, and any mutation error beside it belongs to a
  // file that is no longer selected.
  const avatarUploadMessage = avatarFileRejected ?? avatarUpload.error?.message ?? null;
  /*
   * ⚠ THE BOARD ROW COMES FIRST INSIDE ITS OWN GROUP, AND THE ORDER IS THE
   * CONTROL'S READING ORDER. `groupTargets` builds an `<optgroup>` from
   * CONSECUTIVE options sharing a `groupLabel`, so interleaving two boards
   * here would split one board across two headings.
   *
   * ⚠ `label` IS NOT WHAT THE `<select>` SHOWS. It is what the registration
   * TABLE shows, where no `<optgroup>` heading supplies the board — hence the
   * self-contained `{list} in {board}` pair for lists, and the board's
   * unchanged bare title. `optionLabel` is the in-select override.
   *
   * ⚠ NOT FOLDED INTO THE `loading` PROP BELOW. A pending lists query must not
   * put the whole form behind `reason.checking`: boards are usable the moment
   * `boardsPage` resolves, and the lists fill in beneath them.
   */
  const webhookTargets = webhookBoards.flatMap((board, index) => {
    const groupLabel = board.title;
    // ⚠ THE ID, NOT THE TITLE. Two boards may both be called "Roadmap", and a
    // group broken on the heading merges them into one — both boards' lists
    // under a single "Roadmap" with nothing saying which is which.
    const groupKey = board.id;
    return [
      {
        target: { kind: 'board' as const, id: board.id },
        label: board.title,
        description: board.workspaceName,
        groupLabel,
        groupKey,
        optionLabel: t('integrations.webhook.targetOption.wholeBoard'),
      },
      ...(webhookBoardLists[index]?.data ?? []).map((list) => ({
        target: { kind: 'list' as const, id: list.id },
        label: t('integrations.webhook.targetOption.list', {
          list: list.title,
          board: board.title,
        }),
        description: board.workspaceName,
        groupLabel,
        groupKey,
        optionLabel: list.title,
      })),
    ];
  });

  return (
    <ProfilePage>
      <ProfilePanel
        profile={profile}
        onDisplayNameChange={(next) => {
          displayName.mutate(
            { displayName: next },
            {
              onSuccess: () => {
                announce(t('profile.displayNameSaved'));
              },
            },
          );
        }}
        onBioChange={(next) => {
          bio.mutate(
            { bio: next },
            {
              onSuccess: () => {
                announce(t('profile.bioSaved'));
              },
            },
          );
        }}
        onAvatarChange={(next) => {
          avatar.mutate(
            { avatarUrl: next },
            {
              onSuccess: () => {
                announce(t('profile.avatarSaved'));
              },
            },
          );
        }}
        busy={{
          displayName: displayName.isPending,
          bio: bio.isPending,
          // The upload writes the SAME field as the panel's Save avatar, so the
          // panel's avatar controls are busy while it runs. The mirror of this
          // is `submitDisabled` below; one half without the other closes the
          // race in one direction only.
          avatar: avatar.isPending || avatarUpload.isPending,
        }}
        error={errorProps}
        /*
          FE-44.2 — the file picker, WHERE IT BELONGS as of this chunk.
          `ProfilePanel` renders it inside its own Avatar section, under an
          `h3`, after the URL field it replaces; this route keeps every network
          call. The residual that asked for the move named the prop
          `onAvatarFile`; it shipped as one bundle instead, because two
          optional props that must co-occur can render a file input with no
          button beside it. See `docs/design/fe11-avatar-panel-relocation/AUDIT.md`
          §4b.1.

          ⚠ `submitDisabled` is the OTHER HALF of the two-flight race, and it
          is easy to delete in a refactor with nothing red: two `PATCH
          /me/avatar` flights race, and the avatar the member ends up with is
          whichever round trip finished last.
        */
        avatarUpload={{
          onFile: chooseAvatarFile,
          onSubmit: submitAvatarFile,
          submitDisabled: avatarFile === null || avatar.isPending,
          busy: avatarUpload.isPending,
          message: avatarUploadMessage,
          inputRef: avatarFileInputRef,
        }}
      />
      <AccountLifecycleSection
        busy={{
          deactivate: deactivateAccount.isPending,
          delete: deleteAccount.isPending,
        }}
        error={{
          ...(deactivateAccount.error === null
            ? {}
            : { deactivate: deactivateAccount.error.message }),
          ...(deleteAccount.error === null
            ? {}
            : { delete: deleteAccount.error.message }),
        }}
        onDeactivateAccount={() => {
          deactivateAccount.mutate(undefined, {
            onSuccess: () => {
              announce(t('profile.accountDeactivated'));
              void navigate({ to: SIGN_IN_PATH, replace: true });
            },
          });
        }}
        onDeleteAccount={() => {
          deleteAccount.mutate(undefined, {
            onSuccess: () => {
              announce(t('profile.accountDeleted'));
              void navigate({ to: SIGN_IN_PATH, replace: true });
            },
          });
        }}
      />
      <LocalePreferencesSection
        preferences={localePreferences.data ?? null}
        loading={localePreferences.isLoading}
        error={localePreferences.error?.message ?? null}
        languageBusy={setLocaleLanguage.isPending}
        timezoneBusy={setTimezoneOverride.isPending}
        languageError={setLocaleLanguage.error?.message ?? null}
        timezoneError={setTimezoneOverride.error?.message ?? null}
        onLanguageChange={(language) => {
          setLocaleLanguage.mutate(language, {
            onSuccess: () => {
              announce(t('profile.interfaceLanguageSaved'));
            },
          });
        }}
        onTimezoneChange={(timezoneOverride) => {
          setTimezoneOverride.mutate(timezoneOverride, {
            onSuccess: () => {
              announce(t('profile.timezoneSaved'));
            },
          });
        }}
      />
      <ConnectedToolsSection
        grants={connectedApps.data?.connectedApps}
        loading={connectedApps.isLoading}
        loadError={connectedApps.error?.message}
        createdToken={connectedToolToken}
        createBusy={createConnectedApp.isPending}
        revokeBusyId={
          revokeConnectedApp.isPending ? (revokeConnectedApp.variables?.id ?? null) : null
        }
        createError={createConnectedApp.error?.message}
        revokeErrors={connectedToolErrors}
        // Reachable only if self-service is reopened: while
        // `CONNECTED_APP_SELF_SERVICE_ISSUANCE` is `false` (AT-6) the section
        // renders no create form, and the route would answer 403
        // `self_service_disabled` anyway.
        onCreate={(body) => {
          setConnectedToolToken(null);
          createConnectedApp.mutate(body, {
            onSuccess: (grant) => {
              setConnectedToolToken(grant.token);
              announce(t('profile.connectedToolConnected', { toolName: grant.toolName }));
            },
          });
        }}
        onRevealToken={async (id, action) => {
          // AT-13: the token is returned to the section, which holds it for the
          // life of its dialog. Only the tool's name is announced — and after a
          // microtask, so nothing in the announcement can turn a reveal that
          // already rotated the server's token into a failure the member sees.
          const revealed = await revealConnectedAppToken(id);
          const toolName = revealed.connectedApp.toolName;
          queueMicrotask(() => {
            announce(
              t(
                action === 'reveal'
                  ? 'profile.connectedToolRevealed'
                  : 'profile.connectedToolRegenerated',
                { toolName },
              ),
            );
          });
          return revealed;
        }}
        onRevoke={(id) => {
          setConnectedToolErrors((previous) => {
            if (previous[id] === undefined) return previous;
            const next = { ...previous };
            delete next[id];
            return next;
          });
          revokeConnectedApp.mutate(
            { id },
            {
              onSuccess: (grant) => {
                announce(t('profile.connectedToolRevoked', { toolName: grant.toolName }));
              },
              onError: (error) => {
                setConnectedToolErrors((previous) => ({
                  ...previous,
                  [id]: error.message,
                }));
              },
            },
          );
        }}
      />
      <ChangeRegistrationManager
        registrations={changeRegistrations.data?.changeRegistrations}
        connectedApps={
          connectedApps.data?.connectedApps.map((grant) => ({
            id: grant.id,
            toolName: grant.toolName,
            scope: grant.scope,
            revoked: grant.revokedAt !== null,
          })) ?? []
        }
        targets={webhookTargets}
        loading={
          changeRegistrations.isLoading || boardsPage.isLoading || connectedApps.isLoading
        }
        loadError={
          changeRegistrations.error?.message ??
          boardsPage.error?.message ??
          connectedApps.error?.message
        }
        canManage
        disabledReason={null}
        allowCustomTarget
        createBusy={createChangeRegistration.isPending}
        deleteBusyId={
          deleteChangeRegistration.isPending
            ? (deleteChangeRegistration.variables ?? null)
            : null
        }
        createError={createChangeRegistration.error?.message}
        deleteErrors={changeRegistrationDeleteErrors}
        issuedSecret={issuedSigningSecret}
        onDismissIssuedSecret={() => setIssuedSigningSecret(null)}
        rotateBusyId={
          rotateChangeRegistrationSecret.isPending
            ? (rotateChangeRegistrationSecret.variables ?? null)
            : null
        }
        rotateErrors={changeRegistrationRotateErrors}
        resumeBusyId={
          resumeChangeRegistration.isPending
            ? (resumeChangeRegistration.variables ?? null)
            : null
        }
        resumeErrors={changeRegistrationResumeErrors}
        eventFilterBusyId={
          replaceChangeRegistrationEventFilter.isPending
            ? (replaceChangeRegistrationEventFilter.variables?.registrationId ?? null)
            : null
        }
        eventFilterErrors={changeRegistrationFilterErrors}
        onCreate={(body) => {
          createChangeRegistration.mutate(body, {
            onSuccess: (registration) => {
              // ⚠ THE SECRET IS TAKEN FROM THE MUTATION RESULT, NEVER FROM THE
              // CACHE. `useCreateChangeRegistration` strips it before the
              // splice, and the `invalidateQueries` that follows refetches a
              // list that never carries it — so a panel reading the cache
              // would render and then blank mid-copy, with nothing red.
              setIssuedSigningSecret({
                registrationId: registration.id,
                signingSecret: registration.signingSecret,
              });
              announce(t('profile.webhookCreated'));
            },
          });
        }}
        onRotateSigningSecret={(id) => {
          setChangeRegistrationRotateErrors((previous) => {
            if (previous[id] === undefined) return previous;
            const next = { ...previous };
            delete next[id];
            return next;
          });
          rotateChangeRegistrationSecret.mutate(id, {
            onSuccess: (registration) => {
              setIssuedSigningSecret({
                registrationId: registration.id,
                signingSecret: registration.signingSecret,
              });
              announce(t('profile.webhookSecretRotated'));
            },
            onError: (error) => {
              setChangeRegistrationRotateErrors((previous) => ({
                ...previous,
                [id]: error.message,
              }));
            },
          });
        }}
        onResume={(id) => {
          setChangeRegistrationResumeErrors((previous) => {
            if (previous[id] === undefined) return previous;
            const next = { ...previous };
            delete next[id];
            return next;
          });
          resumeChangeRegistration.mutate(id, {
            onSuccess: () => {
              announce(t('profile.webhookResumed'));
            },
            onError: (error) => {
              setChangeRegistrationResumeErrors((previous) => ({
                ...previous,
                [id]: error.message,
              }));
            },
          });
        }}
        onReplaceEventFilter={(id, eventFilter) => {
          setChangeRegistrationFilterErrors((previous) => {
            if (previous[id] === undefined) return previous;
            const next = { ...previous };
            delete next[id];
            return next;
          });
          replaceChangeRegistrationEventFilter.mutate(
            { registrationId: id, body: { eventFilter } },
            {
              onSuccess: () => {
                announce(t('profile.webhookEventsSaved'));
              },
              onError: (error) => {
                setChangeRegistrationFilterErrors((previous) => ({
                  ...previous,
                  [id]: error.message,
                }));
              },
            },
          );
        }}
        onDelete={(id) => {
          setChangeRegistrationDeleteErrors((previous) => {
            if (previous[id] === undefined) return previous;
            const next = { ...previous };
            delete next[id];
            return next;
          });
          deleteChangeRegistration.mutate(id, {
            onSuccess: () => {
              announce(t('profile.webhookRemoved'));
            },
            onError: (error) => {
              setChangeRegistrationDeleteErrors((previous) => ({
                ...previous,
                [id]: error.message,
              }));
            },
          });
        }}
        onRetry={() => {
          void connectedApps.refetch();
          void changeRegistrations.refetch();
          void boardsPage.refetch();
        }}
      />
    </ProfilePage>
  );
}
